import { useMemo, useState } from 'react';
import { useGetCourseOptionsQuery } from '@teachur/http-client';
import type { CourseOption } from '@teachur/http-client';

const matchesSearch = (course: CourseOption, term: string): boolean =>
  course.name.toLowerCase().includes(term) || (course.code ?? '').toLowerCase().includes(term);

export const useCoursePicker = (categoryId: number | undefined, enabled: boolean) => {
  const optionsQuery = useGetCourseOptionsQuery(categoryId, { enabled });
  const [search, setSearch] = useState('');
  const [selectedById, setSelectedById] = useState<ReadonlyMap<number, CourseOption>>(new Map());

  const filtered = useMemo(() => {
    const all = optionsQuery.data?.courses ?? [];
    const term = search.trim().toLowerCase();
    return term ? all.filter((course) => matchesSearch(course, term)) : all;
  }, [optionsQuery.data, search]);

  const selected = useMemo(() => [...selectedById.values()], [selectedById]);
  const selectedIds = useMemo(() => new Set(selectedById.keys()), [selectedById]);

  const toggle = (course: CourseOption) =>
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (next.has(course.courseId)) {
        next.delete(course.courseId);
      } else {
        next.set(course.courseId, course);
      }
      return next;
    });

  const reset = () => {
    setSelectedById(new Map());
    setSearch('');
  };

  return {
    isLoading: optionsQuery.isLoading,
    isError: optionsQuery.isError,
    search,
    setSearch,
    filtered,
    selected,
    selectedIds,
    toggle,
    reset,
  };
};
